import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tournamentsApi } from '../services/api';
import { Tournament, DeckSubmission, TournamentRegistration } from '../types';

const TournamentSubmissions: React.FC = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'submitted' | 'approved' | 'rejected' | 'players'>('submitted');
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [submissions, setSubmissions] = useState<DeckSubmission[]>([]);
  const [registrations, setRegistrations] = useState<TournamentRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);

  useEffect(() => {
    if (tournamentId) {
      loadData();
    }
  }, [tournamentId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tournamentResponse, submissionsResponse] = await Promise.all([
        tournamentsApi.getById(tournamentId!),
        tournamentsApi.getSubmissions(tournamentId!)
      ]);
      
      setTournament(tournamentResponse.data);
      setSubmissions(submissionsResponse.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRegistrations = async () => {
    if (!tournamentId) return;
    try {
      setLoadingRegistrations(true);
      const response = await tournamentsApi.getTournamentRegistrations(tournamentId);
      setRegistrations(response.data);
    } catch (error) {
      console.error('Failed to load registrations:', error);
      alert('Failed to load registrations. You may not have permission to view this.');
    } finally {
      setLoadingRegistrations(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'players' && tournamentId) {
      loadRegistrations();
    }
  }, [activeTab, tournamentId]);

  const getSubmissionsByStatus = (status: string) => {
    return submissions.filter(submission => submission.status === status);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading submissions...</div>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Tournament not found.</p>
        </div>
      </div>
    );
  }

  const submittedSubmissions = getSubmissionsByStatus('SUBMITTED');
  const approvedSubmissions = getSubmissionsByStatus('APPROVED');
  const rejectedSubmissions = getSubmissionsByStatus('REJECTED');

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/tournament-management')}
            className="text-gray-500 hover:text-gray-700 mb-4"
          >
            ← Back to Tournament Management
          </button>
          <h1 className="text-3xl font-bold text-gray-900">{tournament.name}</h1>
          <p className="text-gray-600 mt-2">Tournament Submissions</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('submitted')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'submitted'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Submitted ({submittedSubmissions.length})
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'approved'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Approved ({approvedSubmissions.length})
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'rejected'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Rejected ({rejectedSubmissions.length})
            </button>
            <button
              onClick={() => setActiveTab('players')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'players'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Players ({registrations.length})
            </button>
          </nav>
        </div>

        {/* Content */}
        {activeTab === 'players' ? (
          <PlayerOverview 
            tournamentId={tournamentId!}
            registrations={registrations}
            loading={loadingRegistrations}
            onRefresh={loadRegistrations}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeTab === 'submitted' && submittedSubmissions.map((submission) => (
                <SubmissionCard
                  key={submission.id}
                  submission={submission}
                  onClick={() => navigate(`/deck/${submission.id}`)}
                />
              ))}
              
              {activeTab === 'approved' && approvedSubmissions.map((submission) => (
                <SubmissionCard
                  key={submission.id}
                  submission={submission}
                  onClick={() => navigate(`/deck/${submission.id}`)}
                />
              ))}
              
              {activeTab === 'rejected' && rejectedSubmissions.map((submission) => (
                <SubmissionCard
                  key={submission.id}
                  submission={submission}
                  onClick={() => navigate(`/deck/${submission.id}`)}
                />
              ))}
            </div>

            {/* Empty state */}
            {((activeTab === 'submitted' && submittedSubmissions.length === 0) ||
              (activeTab === 'approved' && approvedSubmissions.length === 0) ||
              (activeTab === 'rejected' && rejectedSubmissions.length === 0)) && (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">
                  No {activeTab} submissions found.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

interface SubmissionCardProps {
  submission: DeckSubmission;
  onClick: () => void;
}

const SubmissionCard: React.FC<SubmissionCardProps> = ({ submission, onClick }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div
      className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={onClick}
    >
      <h3 className="text-xl font-semibold text-gray-800 mb-2">{submission.deck_name}</h3>
      
      <div className="space-y-2 mb-4">
        <div className="flex justify-between">
          <span className="text-sm text-gray-500">Player:</span>
          <span className="text-sm font-medium">
            {submission.user?.username || `User ${submission.user_id}`}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-sm text-gray-500">Status:</span>
          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}>
            {submission.status}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-sm text-gray-500">Cost:</span>
          <span className="text-sm font-medium">€{submission.total_cost.toFixed(2)}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-sm text-gray-500">Submitted:</span>
          <span className="text-sm font-medium">{formatDateTime(submission.created_at)}</span>
        </div>

        {submission.rejection_reason && (
          <div className="mt-2">
            <span className="text-sm text-gray-500">Rejection Reason:</span>
            <p className="text-sm text-gray-700 mt-1">{submission.rejection_reason}</p>
          </div>
        )}
      </div>
      
      <div className="text-center">
        <span className="text-blue-600 text-sm font-medium">Click to view details</span>
      </div>
    </div>
  );
};

interface PlayerOverviewProps {
  tournamentId: string;
  registrations: TournamentRegistration[];
  loading: boolean;
  onRefresh: () => void;
}

const PlayerOverview: React.FC<PlayerOverviewProps> = ({ tournamentId, registrations, loading, onRefresh }) => {
  const [processing, setProcessing] = useState<Set<string>>(new Set());

  const handleAccept = async (userId: number, wasRejected: boolean = false) => {
    if (wasRejected) {
      const confirmed = window.confirm(
        'Warning: This player was previously rejected. Are you sure you want to accept them now?'
      );
      if (!confirmed) {
        return;
      }
    }
    
    try {
      setProcessing(prev => new Set(prev).add(`accept-${userId}`));
      await tournamentsApi.acceptRegistration(tournamentId, userId);
      onRefresh();
    } catch (error) {
      console.error('Failed to accept registration:', error);
      alert('Failed to accept registration. Please try again.');
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(`accept-${userId}`);
        return newSet;
      });
    }
  };

  const handleReject = async (userId: number) => {
    try {
      setProcessing(prev => new Set(prev).add(`reject-${userId}`));
      await tournamentsApi.rejectRegistration(tournamentId, userId);
      onRefresh();
    } catch (error) {
      console.error('Failed to reject registration:', error);
      alert('Failed to reject registration. Please try again.');
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(`reject-${userId}`);
        return newSet;
      });
    }
  };

  const pendingRegistrations = registrations.filter(r => r.status === 'PENDING');
  const acceptedRegistrations = registrations.filter(r => r.status === 'ACCEPTED');
  const rejectedRegistrations = registrations.filter(r => r.status === 'REJECTED');

  const getStatusBadge = (status: string) => {
    const config = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      ACCEPTED: { color: 'bg-green-100 text-green-800', text: 'Accepted' },
      REJECTED: { color: 'bg-red-100 text-red-800', text: 'Rejected' },
    };
    const badgeConfig = config[status as keyof typeof config] || config.PENDING;
    return (
      <span className={`px-2 py-1 text-xs rounded ${badgeConfig.color}`}>
        {badgeConfig.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="text-center text-gray-600">Loading registrations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending Registrations */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Pending Applications ({pendingRegistrations.length})
        </h3>
        {pendingRegistrations.length === 0 ? (
          <p className="text-sm text-gray-500">No pending applications</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingRegistrations.map((reg) => (
              <div key={reg.id} className="bg-white rounded-lg shadow-md p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800 mb-1">
                      {reg.user?.username || `User #${reg.user_id}`}
                    </div>
                    <div className="text-sm text-gray-500 mb-2">
                      {reg.user?.email || 'No email'}
                    </div>
                    <div className="text-xs text-gray-400">
                      Applied: {new Date(reg.created_at).toLocaleString()}
                    </div>
                  </div>
                  {getStatusBadge(reg.status)}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAccept(reg.user_id, false)}
                    disabled={processing.has(`accept-${reg.user_id}`)}
                    className="flex-1 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                  >
                    {processing.has(`accept-${reg.user_id}`) ? 'Accepting...' : 'Accept'}
                  </button>
                  <button
                    onClick={() => handleReject(reg.user_id)}
                    disabled={processing.has(`reject-${reg.user_id}`)}
                    className="flex-1 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm disabled:opacity-50"
                  >
                    {processing.has(`reject-${reg.user_id}`) ? 'Rejecting...' : 'Reject'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Accepted Registrations */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Accepted Players ({acceptedRegistrations.length})
        </h3>
        {acceptedRegistrations.length === 0 ? (
          <p className="text-sm text-gray-500">No accepted players yet</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {acceptedRegistrations.map((reg) => (
              <div key={reg.id} className="bg-white rounded-lg shadow-md p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800 mb-1">
                      {reg.user?.username || `User #${reg.user_id}`}
                    </div>
                    <div className="text-sm text-gray-500 mb-2">
                      {reg.user?.email || 'No email'}
                    </div>
                    <div className="text-xs text-gray-400">
                      Accepted: {new Date(reg.updated_at || reg.created_at).toLocaleString()}
                    </div>
                  </div>
                  {getStatusBadge(reg.status)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rejected Registrations */}
      {rejectedRegistrations.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Rejected Applications ({rejectedRegistrations.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rejectedRegistrations.map((reg) => (
              <div key={reg.id} className="bg-white rounded-lg shadow-md p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800 mb-1">
                      {reg.user?.username || `User #${reg.user_id}`}
                    </div>
                    <div className="text-sm text-gray-500 mb-2">
                      {reg.user?.email || 'No email'}
                    </div>
                    <div className="text-xs text-gray-400">
                      Rejected: {new Date(reg.updated_at || reg.created_at).toLocaleString()}
                    </div>
                  </div>
                  {getStatusBadge(reg.status)}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAccept(reg.user_id, true)}
                    disabled={processing.has(`accept-${reg.user_id}`)}
                    className="flex-1 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                  >
                    {processing.has(`accept-${reg.user_id}`) ? 'Accepting...' : 'Accept'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentSubmissions;
